import React, { useState } from "react";
import classes from "./SurveyForm.module.css";
import SideBarSkeleton from "components/Core/SideBarSkeleton";
import Box from "components/Core/Box";
import { DropDown } from "components/Core/DropDown";
import {
  howILearnBestOptions,
  languageSupportLevelOptions,
  learningLanguagesOptions,
  otherLanguagesOptions,
} from "constant/data";
import { useSelector } from "react-redux";
import { Checkbox } from "components/Core/Checkbox";
import { Button } from "components/Core/Button";
import { BaseURL } from "config/apiUrl";
import { Patch } from "axios/AxiosFunctions";
import {
  apiHeader,
  RenderToast,
  validateNestedParams,
} from "helper/HelperFunction";
import { useDispatch } from "react-redux";
import { signOutRequest, updateUser } from "store/auth/authSlice";
import HeadingComponent from "components/HeadingComponent";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { Label } from "components/Core/Label";

const SurveyForm = () => {
  const { access_token, user } = useSelector((state) => state.authReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // part 1
  const [learningLanguages, setLearningLanguages] = useState(
    matchedOptionsWithArray(user?.survey?.learningLanguages || [])
  );
  const [familyLanguages, setFamilyLanguages] = useState(
    matchedOptionsWithArray(user?.survey?.familyLanguages || [])
  );
  const [thinkingLanguages, setThinkingLanguages] = useState(
    matchedOptionsWithArray(user?.survey?.thinkingLanguages || [])
  );
  const [friendsLanguages, setFriendsLanguages] = useState(
    matchedOptionsWithArray(user?.survey?.friendsLanguages || [])
  );
  const [cultureLanguages, setCultureLanguages] = useState(
    matchedOptionsWithArray(user?.survey?.cultureLanguages || [])
  );

  // part 2
  const [englishSupportLevel, setEnglishSupportLevel] = useState(
    matchedOptions(user?.survey?.englishSupportLevel)
  );
  const [languageSupportLevel, setLanguageSupportLevel] = useState(
    matchedOptions(user?.survey?.languageSupportLevel)
  );
  const [cultureSupportLevel, setCultureSupportLevel] = useState(
    matchedOptions(user?.survey?.cultureSupportLevel)
  );
  const [schoolSupportLevel, setSchoolSupportLevel] = useState(
    matchedOptions(user?.survey?.schoolSupportLevel)
  );

  //   part 3
  const generalParsed = parseLearnBest(
    user?.survey?.general,
    howILearnBestOptions?.general
  );
  const [general, setGeneral] = useState(generalParsed.values);
  const [generalOtherText, setGeneralOtherText] = useState(
    generalParsed.otherText
  );
  const readingParsed = parseLearnBest(
    user?.survey?.reading,
    howILearnBestOptions?.reading
  );
  const [reading, setReading] = useState(readingParsed.values);
  const [readingOtherText, setReadingOtherText] = useState(
    readingParsed.otherText
  );
  const listeningParsed = parseLearnBest(
    user?.survey?.listening,
    howILearnBestOptions?.listening
  );
  const [listening, setListening] = useState(listeningParsed.values);
  const [listeningOtherText, setListeningOtherText] = useState(
    listeningParsed.otherText
  );
  const writingParsed = parseLearnBest(
    user?.survey?.writing,
    howILearnBestOptions?.writing
  );
  const [writing, setWriting] = useState(writingParsed.values);
  const [writingOtherText, setWritingOtherText] = useState(
    writingParsed.otherText
  );
  const speakingParsed = parseLearnBest(
    user?.survey?.speaking,
    howILearnBestOptions?.speaking
  );
  const [speaking, setSpeaking] = useState(speakingParsed.values);
  const [speakingOtherText, setSpeakingOtherText] = useState(
    speakingParsed.otherText
  );

  //   loading
  const [loading, setLoading] = useState(false);

  const processLearnBest = (arr, otherText) => {
    return arr?.map((item) =>
      item === "Other" ? otherText?.trim() || "Other" : item
    );
  };

  const handleSubmit = async () => {
    const url = BaseURL(`users/fill-survey`);
    const params = {
      learningLanguages: learningLanguages?.map((item) => item?.value),
      familyLanguages: familyLanguages?.map((item) => item?.value),
      thinkingLanguages: thinkingLanguages?.map((item) => item?.value),
      friendsLanguages: friendsLanguages?.map((item) => item?.value),
      cultureLanguages: cultureLanguages?.map((item) => item?.value),
      englishSupportLevel: englishSupportLevel?.value,
      languageSupportLevel: languageSupportLevel?.value,
      cultureSupportLevel: cultureSupportLevel?.value,
      schoolSupportLevel: schoolSupportLevel?.value,
      general: processLearnBest(general, generalOtherText),
      reading: processLearnBest(reading, readingOtherText),
      listening: processLearnBest(listening, listeningOtherText),
      writing: processLearnBest(writing, writingOtherText),
      speaking: processLearnBest(speaking, speakingOtherText),
    };
    if (!validateNestedParams(params)) return;
    setLoading(true);
    const response = await Patch(url, params, apiHeader(access_token));
    if (response !== undefined) {
      RenderToast("success", "Survey form filled successfully");
      dispatch(updateUser(response?.data));
      navigate("/");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    dispatch(signOutRequest());
    navigate("/login");
  };

  return (
    <div className={classes.surveyFormContainer}>
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.headerContentLeft}>
            {user?.surveyFilled && <FiArrowLeft onClick={() => navigate(-1)} />}
            <HeadingComponent heading={"Survey Form"} />
          </div>
          <p>
            Please fill out the survey form to continue and help us better
            understand your language learning journey.
          </p>
        </div>
        <div className={classes.profileSection}>
          <Button label="Logout" onClick={handleLogout} variant="secondary" />
        </div>
      </div>
      <div className={classes.partWrapper}>
        <h6 className={classes.title}>My Languages</h6>
        <Box className={classes.boxWrapper}>
          <div className={classes.addLanguageWrapper}>
            <Label>
              Which language(s) do you use for learning something new?
            </Label>
            <DropDown
              placeholder={"Select Language"}
              value={learningLanguages}
              setter={setLearningLanguages}
              options={learningLanguagesOptions}
              isMulti={true}
            />
          </div>

          <div className={classes.addLanguageWrapper}>
            <Label>
              When you are thinking to yourself, which language(s) do you use?
            </Label>
            <DropDown
              placeholder={"Select Language"}
              value={thinkingLanguages}
              setter={setThinkingLanguages}
              options={learningLanguagesOptions}
              isMulti={true}
            />
          </div>

          <div className={classes.addLanguageWrapper}>
            <Label>Which language(s) do you use with friends?</Label>
            <DropDown
              placeholder={"Select Language"}
              value={friendsLanguages}
              setter={setFriendsLanguages}
              options={learningLanguagesOptions}
              isMulti={true}
            />
          </div>

          <div className={classes.addLanguageWrapper}>
            <Label>
              Which language(s) connect to your culture and family traditions?
            </Label>
            <DropDown
              placeholder={"Select Language"}
              value={cultureLanguages}
              setter={setCultureLanguages}
              options={learningLanguagesOptions}
              isMulti={true}
            />
          </div>
        </Box>
      </div>
      <div className={classes.partWrapper}>
        <h6 className={classes.title}>Family Languages</h6>
        <Box className={classes.boxWrapper}>
          <div className={classes.addLanguageWrapper}>
            <Label>
              Which language do you use to talk with your family at home
            </Label>
            <DropDown
              placeholder={"Select Language"}
              value={familyLanguages}
              setter={setFamilyLanguages}
              options={learningLanguagesOptions}
              isMulti={true}
            />
          </div>
        </Box>
      </div>
      <div className={classes.partWrapper}>
        <h6 className={classes.title}>Language & Culture</h6>
        <p className={classes.subtitle}>
          When thinking about your school, rate the following
        </p>
        <Box className={classes.boxWrapper}>
          <DropDown
            label={"I feel supported in learning English."}
            placeholder={"Select Level"}
            value={englishSupportLevel}
            setter={setEnglishSupportLevel}
            options={languageSupportLevelOptions}
          />
          <DropDown
            label={"I feel supported in learning my home language."}
            placeholder={"Select Level"}
            value={languageSupportLevel}
            setter={setLanguageSupportLevel}
            options={languageSupportLevelOptions}
          />
          <DropDown
            label={"I feel proud of my culture and the languages I speak."}
            placeholder={"Select Level"}
            value={cultureSupportLevel}
            setter={setCultureSupportLevel}
            options={languageSupportLevelOptions}
          />
          <DropDown
            label={
              "My school celebrates and respects different languages and cultures."
            }
            placeholder={"Select Level"}
            value={schoolSupportLevel}
            setter={setSchoolSupportLevel}
            options={languageSupportLevelOptions}
          />
        </Box>
      </div>
      <div className={classes.partWrapper}>
        <h6 className={classes.title}>How I Learn Best</h6>
        <p>
          Select 1-2 strategies from each category about how you learn best.
        </p>
        <Box className={classes.boxWrapper}>
          <div className={classes.checkboxWrapper}>
            <p className={classes.checkboxTitle}>General</p>
            {howILearnBestOptions?.general?.map((item) => (
              <Checkbox
                label={item}
                key={item}
                value={general}
                setValue={(e) => {
                  if (!validateLearnBest(e)) return;
                  setGeneral(e);
                }}
              />
            ))}
            {general?.includes("Other") && (
              <input
                className={classes.otherTextInput}
                placeholder="Please describe how you learn best"
                value={generalOtherText}
                onChange={(e) => setGeneralOtherText(e.target.value)}
              />
            )}
          </div>
          <div className={classes.checkboxWrapper}>
            <p className={classes.checkboxTitle}>Reading</p>
            {howILearnBestOptions?.reading?.map((item) => (
              <Checkbox
                label={item}
                key={item}
                value={reading}
                setValue={(e) => {
                  if (!validateLearnBest(e)) return;
                  setReading(e);
                }}
              />
            ))}
            {reading?.includes("Other") && (
              <input
                className={classes.otherTextInput}
                placeholder="Please describe how you learn best"
                value={readingOtherText}
                onChange={(e) => setReadingOtherText(e.target.value)}
              />
            )}
          </div>
          <div className={classes.checkboxWrapper}>
            <p className={classes.checkboxTitle}>Writing</p>
            {howILearnBestOptions?.writing?.map((item) => (
              <Checkbox
                label={item}
                key={item}
                value={writing}
                setValue={(e) => {
                  if (!validateLearnBest(e)) return;
                  setWriting(e);
                }}
              />
            ))}
            {writing?.includes("Other") && (
              <input
                className={classes.otherTextInput}
                placeholder="Please describe how you learn best"
                value={writingOtherText}
                onChange={(e) => setWritingOtherText(e.target.value)}
              />
            )}
          </div>
          <div className={classes.checkboxWrapper}>
            <p className={classes.checkboxTitle}>Speaking</p>
            {howILearnBestOptions?.speaking?.map((item) => (
              <Checkbox
                label={item}
                key={item}
                value={speaking}
                setValue={(e) => {
                  if (!validateLearnBest(e)) return;
                  setSpeaking(e);
                }}
              />
            ))}
            {speaking?.includes("Other") && (
              <input
                className={classes.otherTextInput}
                placeholder="Please describe how you learn best"
                value={speakingOtherText}
                onChange={(e) => setSpeakingOtherText(e.target.value)}
              />
            )}
          </div>
          <div className={classes.checkboxWrapper}>
            <p className={classes.checkboxTitle}>Listening</p>
            {howILearnBestOptions?.listening?.map((item) => (
              <Checkbox
                label={item}
                value={listening}
                key={item}
                setValue={(e) => {
                  if (!validateLearnBest(e)) return;
                  setListening(e);
                }}
              />
            ))}
            {listening?.includes("Other") && (
              <input
                className={classes.otherTextInput}
                placeholder="Please describe how you learn best"
                value={listeningOtherText}
                onChange={(e) => setListeningOtherText(e.target.value)}
              />
            )}
          </div>
        </Box>
      </div>
      <div className={classes.btnWrapper}>
        <Button
          label={loading ? "Saving..." : "Save"}
          onClick={handleSubmit}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default SurveyForm;

const matchedOptionsWithArray = (value, options = learningLanguagesOptions) => {
  const result = [];
  for (const item of value) {
    const opt = options?.find((o) => o?.value === item);
    if (opt) {
      result.push(opt);
    } else {
      result.push({
        label: item,
        value: item,
      });
    }
  }
  return result;
};

const matchedOptions = (value, options = languageSupportLevelOptions) => {
  const opt = options?.find((item) => item?.value === value);
  if (opt) {
    return opt;
  }
  return {
    label: value,
    value: value,
  };
};

// Saved values replace the literal "Other" checkbox value with whatever
// free text the student typed (see processLearnBest), so on reload we need
// to reverse that: anything not in the category's known options is the
// custom "Other" text, and gets swapped back to "Other" for the checkbox.
const parseLearnBest = (savedValues = [], knownOptions = []) => {
  const values = [];
  let otherText = "";
  (savedValues || []).forEach((item) => {
    if (knownOptions?.includes(item)) {
      values.push(item);
    } else {
      values.push("Other");
      otherText = item;
    }
  });
  return { values, otherText };
};

const validateLearnBest = (value) => {
  if (value?.length > 2) {
    RenderToast("error", "You can only select 2 strategies from each category");
    return false;
  }
  return true;
};
